import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "../../components/AppLayout";
import { api } from "../../api/client";
import { Edit, Trash2, Plus, Eye, X, Save, BookOpen, ListChecks, ImageIcon, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useNavigate } from "react-router-dom";

const LEVELS = ["Beginner", "Intermediate", "Advanced"];
const CATEGORIES = ["Cybersecurity", "Close Protection", "Travel Safety", "Tactical", "Risk Management", "Maritime"];

function emptyCourse() {
    return {
        title: "", category: "Cybersecurity", level: "Beginner",
        price: 199, original_price: 399, sale_percent: 50,
        duration_hours: 8, description: "",
        thumbnail: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=940&auto=format&fit=crop",
    };
}

export default function AdminCourses() {
    const navigate = useNavigate();
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editor, setEditor] = useState(null); // {mode, course, tab}
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [search, setSearch] = useState("");

    const load = async () => {
        try { setCourses(await api.get("/admin/courses")); } catch { toast.error("Failed to load courses"); }
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() =>
        courses.filter((c) => !search || (c.title + c.category).toLowerCase().includes(search.toLowerCase()))
    , [courses, search]);

    const openCreate = () => setEditor({ mode: "create", course: emptyCourse(), tab: "overview" });
    const openEdit = async (c) => {
        try {
            const full = await api.get(`/admin/courses/${c.id}`);
            setEditor({ mode: "edit", course: full, tab: "overview" });
        } catch { toast.error("Failed to load course"); }
    };

    const handleDelete = async () => {
        try {
            await api.del(`/admin/courses/${confirmDelete.id}`);
            toast.success("Course deleted");
            setConfirmDelete(null);
            load();
        } catch { toast.error("Delete failed"); }
    };

    return (
        <AppLayout searchPlaceholder="Search courses...">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                <div>
                    <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Course Management</h1>
                    <p className="text-sm text-gray-400 mt-2">{courses.length} courses published • Manage curriculum, quizzes and pricing</p>
                </div>
                <button data-testid="create-course" onClick={openCreate} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-lime text-ink-900 text-sm font-bold hover:bg-lime-hover transition-all hover:-translate-y-0.5">
                    <Plus className="w-4 h-4" /> Create New Course
                </button>
            </div>

            <div className="mb-6 flex flex-wrap items-center gap-3">
                <input
                    data-testid="course-search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Filter by title or category..."
                    className="flex-1 max-w-md bg-ink-700 border border-white/5 rounded-full px-5 py-3 text-sm focus:outline-none focus:border-lime/40"
                />
                <span className="text-xs text-gray-500">{filtered.length} match</span>
            </div>

            {loading ? <div className="text-gray-500">Loading…</div> : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {filtered.map((c) => (
                        <div key={c.id} data-testid={`admin-course-${c.id}`} className="bg-ink-700 rounded-2xl border border-white/5 overflow-hidden group hover:border-white/15 transition-colors">
                            <div className="relative aspect-[4/3] overflow-hidden">
                                <img src={c.thumbnail} alt={c.title} className="w-full h-full object-cover" />
                                <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-ink-900/80 backdrop-blur-sm text-xs font-semibold border border-white/10">{c.category}</span>
                                <span className="absolute top-3 right-3 px-3 py-1 rounded-full bg-lime/90 text-ink-900 text-xs font-bold">£{c.price}</span>
                            </div>
                            <div className="p-5">
                                <h3 className="font-display font-bold text-lg leading-tight line-clamp-2">{c.title}</h3>
                                <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                                    <div><div className="text-gray-500">Students</div><div className="font-bold mt-0.5">{(c.students / 1000).toFixed(1)}k</div></div>
                                    <div><div className="text-gray-500">Rating</div><div className="font-bold mt-0.5 text-lime">{c.rating || "—"}</div></div>
                                    <div><div className="text-gray-500">Hours</div><div className="font-bold mt-0.5">{c.duration_hours}</div></div>
                                </div>
                                <div className="mt-5 pt-4 border-t border-white/5 flex items-center gap-2">
                                    <button data-testid={`view-${c.id}`} onClick={() => navigate(`/catalog/${c.id}`)} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border border-white/10 text-xs font-semibold hover:bg-white/5 transition-colors">
                                        <Eye className="w-3.5 h-3.5" /> View
                                    </button>
                                    <button data-testid={`edit-${c.id}`} onClick={() => openEdit(c)} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-grape text-white text-xs font-semibold hover:bg-grape-hover transition-colors">
                                        <Edit className="w-3.5 h-3.5" /> Edit
                                    </button>
                                    <button data-testid={`delete-${c.id}`} onClick={() => setConfirmDelete(c)} className="w-9 h-9 rounded-full bg-ink-600 hover:bg-red-500/20 hover:text-red-400 text-gray-400 flex items-center justify-center transition-colors">
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {editor && <CourseEditor editor={editor} setEditor={setEditor} onSaved={() => { setEditor(null); load(); }} />}

            {confirmDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-6" onClick={() => setConfirmDelete(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-ink-700 rounded-3xl border border-white/10 p-7">
                        <h3 className="font-display font-bold text-xl">Delete course?</h3>
                        <p className="text-sm text-gray-400 mt-2">This will permanently remove "<strong>{confirmDelete.title}</strong>" along with its curriculum, quiz and existing enrollments. Cannot be undone.</p>
                        <div className="mt-5 flex justify-end gap-3">
                            <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 rounded-full border border-white/10 text-sm">Cancel</button>
                            <button data-testid="confirm-delete-course" onClick={handleDelete} className="px-4 py-2 rounded-full bg-red-500 text-white text-sm font-bold">Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </AppLayout>
    );
}

function CourseEditor({ editor, setEditor, onSaved }) {
    const { mode, course, tab } = editor;
    const [c, setC] = useState(course);
    const [saving, setSaving] = useState(false);

    useEffect(() => { setC(course); }, [course?.id]); // eslint-disable-line

    const setField = (k, v) => setC({ ...c, [k]: v });

    const save = async () => {
        setSaving(true);
        try {
            if (mode === "create") {
                const created = await api.post("/admin/courses", {
                    title: c.title, category: c.category, level: c.level,
                    price: parseFloat(c.price), original_price: parseFloat(c.original_price || 0),
                    sale_percent: parseInt(c.sale_percent || 0), duration_hours: parseFloat(c.duration_hours),
                    thumbnail: c.thumbnail, description: c.description,
                });
                toast.success("Course created");
                setC({ ...c, id: created.id, curriculum: [], quiz: { title: `${c.title} Quiz`, questions: [] } });
                setEditor({ mode: "edit", course: { ...c, id: created.id, curriculum: [], quiz: { title: `${c.title} Quiz`, questions: [] } }, tab: "overview" });
            } else {
                await api.put(`/admin/courses/${c.id}`, {
                    title: c.title, category: c.category, level: c.level,
                    price: parseFloat(c.price), original_price: parseFloat(c.original_price || 0),
                    sale_percent: parseInt(c.sale_percent || 0), duration_hours: parseFloat(c.duration_hours),
                    thumbnail: c.thumbnail, cover: c.cover, description: c.description,
                    instructor_name: c.instructor_name, instructor_title: c.instructor_title,
                });
                toast.success("Course saved");
                onSaved();
            }
        } catch (e) { toast.error("Save failed: " + (e.message || "")); }
        setSaving(false);
    };

    const saveCurriculum = async () => {
        setSaving(true);
        try {
            await api.put(`/admin/courses/${c.id}/curriculum`, { stages: c.curriculum || [] });
            toast.success("Curriculum saved");
        } catch { toast.error("Failed"); }
        setSaving(false);
    };

    const saveQuiz = async () => {
        setSaving(true);
        try {
            await api.put(`/admin/courses/${c.id}/quiz`, { title: c.quiz.title, questions: c.quiz.questions });
            toast.success("Quiz saved");
        } catch { toast.error("Failed"); }
        setSaving(false);
    };

    const tabs = [
        { id: "overview", label: "Overview", icon: ImageIcon },
        ...(mode === "edit" ? [
            { id: "curriculum", label: "Curriculum", icon: BookOpen },
            { id: "quiz", label: "Quiz", icon: ListChecks },
            { id: "pricing", label: "Pricing", icon: Sparkles },
        ] : []),
    ];

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm p-4 flex items-center justify-center" onClick={() => setEditor(null)}>
            <div onClick={(e) => e.stopPropagation()} data-testid="course-editor" className="w-full max-w-4xl max-h-[90vh] bg-ink-700 rounded-3xl border border-white/10 flex flex-col overflow-hidden">
                <div className="flex items-center justify-between p-6 border-b border-white/5">
                    <div>
                        <h2 className="font-display font-bold text-xl">{mode === "create" ? "New Course" : "Edit Course"}</h2>
                        <p className="text-xs text-gray-400 mt-1">{c.title || "Untitled course"}</p>
                    </div>
                    <button onClick={() => setEditor(null)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
                </div>

                <div className="flex border-b border-white/5 px-4">
                    {tabs.map((t) => {
                        const Icon = t.icon;
                        return (
                            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setEditor({ ...editor, tab: t.id })}
                                className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors ${tab === t.id ? "border-lime text-lime" : "border-transparent text-gray-400 hover:text-white"}`}>
                                <Icon className="w-4 h-4" /> {t.label}
                            </button>
                        );
                    })}
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    {tab === "overview" && <OverviewTab c={c} setField={setField} />}
                    {tab === "curriculum" && <CurriculumTab c={c} setC={setC} />}
                    {tab === "quiz" && <QuizTab c={c} setC={setC} />}
                    {tab === "pricing" && <PricingTab c={c} setField={setField} />}
                </div>

                <div className="p-5 border-t border-white/5 flex justify-end gap-3">
                    <button onClick={() => setEditor(null)} className="px-5 py-2.5 rounded-full border border-white/10 text-sm font-semibold hover:bg-white/5">Cancel</button>
                    {tab === "curriculum" && mode === "edit" ? (
                        <button data-testid="save-curriculum" onClick={saveCurriculum} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-lime text-ink-900 text-sm font-bold disabled:opacity-50"><Save className="w-4 h-4" /> Save Curriculum</button>
                    ) : tab === "quiz" && mode === "edit" ? (
                        <button data-testid="save-quiz" onClick={saveQuiz} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-lime text-ink-900 text-sm font-bold disabled:opacity-50"><Save className="w-4 h-4" /> Save Quiz</button>
                    ) : (
                        <button data-testid="save-course" onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-lime text-ink-900 text-sm font-bold disabled:opacity-50"><Save className="w-4 h-4" /> {mode === "create" ? "Create" : "Save"}</button>
                    )}
                </div>
            </div>
        </div>
    );
}

const inputCls = "w-full bg-ink-800 border border-white/5 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-lime/40";
const labelCls = "block text-xs uppercase tracking-widest text-gray-500 mb-2 font-semibold";

function OverviewTab({ c, setField }) {
    return (
        <div className="space-y-5">
            <div>
                <label className={labelCls}>Course title</label>
                <input data-testid="course-title" value={c.title} onChange={(e) => setField("title", e.target.value)} className={inputCls} />
            </div>
            <div>
                <label className={labelCls}>Description</label>
                <textarea data-testid="course-description" value={c.description || ""} onChange={(e) => setField("description", e.target.value)} rows={4} className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className={labelCls}>Category</label>
                    <Select value={c.category} onValueChange={(v) => setField("category", v)}>
                        <SelectTrigger data-testid="course-category" className="bg-ink-800 border-white/5 rounded-xl h-11"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-ink-800 border-white/10 text-white">
                            {CATEGORIES.map((x) => <SelectItem key={x} value={x} className="focus:bg-ink-700 focus:text-white">{x}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <label className={labelCls}>Level</label>
                    <Select value={c.level} onValueChange={(v) => setField("level", v)}>
                        <SelectTrigger data-testid="course-level" className="bg-ink-800 border-white/5 rounded-xl h-11"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-ink-800 border-white/10 text-white">
                            {LEVELS.map((x) => <SelectItem key={x} value={x} className="focus:bg-ink-700 focus:text-white">{x}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className={labelCls}>Duration (hours)</label>
                    <input type="number" data-testid="course-duration" value={c.duration_hours} onChange={(e) => setField("duration_hours", e.target.value)} className={inputCls} />
                </div>
                <div>
                    <label className={labelCls}>Thumbnail URL</label>
                    <input data-testid="course-thumbnail" value={c.thumbnail || ""} onChange={(e) => setField("thumbnail", e.target.value)} className={inputCls} />
                </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className={labelCls}>Instructor name</label>
                    <input value={c.instructor_name || "Nige Thomas"} onChange={(e) => setField("instructor_name", e.target.value)} className={inputCls} />
                </div>
                <div>
                    <label className={labelCls}>Instructor title</label>
                    <input value={c.instructor_title || "BMG Course Instructor"} onChange={(e) => setField("instructor_title", e.target.value)} className={inputCls} />
                </div>
            </div>
        </div>
    );
}

function PricingTab({ c, setField }) {
    return (
        <div className="space-y-5 max-w-md">
            <div>
                <label className={labelCls}>Price (£)</label>
                <input type="number" data-testid="course-price" value={c.price} onChange={(e) => setField("price", e.target.value)} className={inputCls} />
            </div>
            <div>
                <label className={labelCls}>Original price (£)</label>
                <input type="number" value={c.original_price || ""} onChange={(e) => setField("original_price", e.target.value)} className={inputCls} />
            </div>
            <div>
                <label className={labelCls}>Sale % off</label>
                <input type="number" value={c.sale_percent || 0} onChange={(e) => setField("sale_percent", e.target.value)} className={inputCls} />
            </div>
        </div>
    );
}

function CurriculumTab({ c, setC }) {
    const stages = c.curriculum || [];
    const setStages = (s) => setC({ ...c, curriculum: s });
    const addStage = () => setStages([...stages, { stage: `Stage ${stages.length + 1}: New Stage`, lessons: [] }]);
    const removeStage = (i) => setStages(stages.filter((_, idx) => idx !== i));
    const setStageTitle = (i, title) => setStages(stages.map((s, idx) => idx === i ? { ...s, stage: title } : s));
    const addLesson = (i) => {
        const id = `l_${Date.now()}`;
        setStages(stages.map((s, idx) => idx === i ? { ...s, lessons: [...s.lessons, { id, title: "New lesson", duration: "00:00", video: "" }] } : s));
    };
    const setLesson = (si, li, field, val) => {
        setStages(stages.map((s, idx) => idx === si ? {
            ...s, lessons: s.lessons.map((l, lidx) => lidx === li ? { ...l, [field]: val } : l),
        } : s));
    };
    const removeLesson = (si, li) => setStages(stages.map((s, idx) => idx === si ? { ...s, lessons: s.lessons.filter((_, lidx) => lidx !== li) } : s));

    return (
        <div className="space-y-4">
            {stages.length === 0 && <p className="text-sm text-gray-500">No stages yet. Add the first one to begin building the curriculum.</p>}
            {stages.map((s, i) => (
                <div key={i} className="bg-ink-800 border border-white/5 rounded-2xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <input value={s.stage} onChange={(e) => setStageTitle(i, e.target.value)} className="flex-1 bg-transparent border-b border-white/10 px-2 py-1 text-sm font-bold focus:outline-none focus:border-lime" />
                        <button data-testid={`remove-stage-${i}`} onClick={() => removeStage(i)} className="w-8 h-8 rounded-full hover:bg-red-500/20 hover:text-red-400 text-gray-400 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="space-y-2">
                        {s.lessons.map((l, li) => (
                            <div key={li} className="flex items-center gap-2 bg-ink-700 rounded-xl p-2.5">
                                <input value={l.title} onChange={(e) => setLesson(i, li, "title", e.target.value)} placeholder="Lesson title" className="flex-1 bg-transparent text-sm focus:outline-none" />
                                <input value={l.duration} onChange={(e) => setLesson(i, li, "duration", e.target.value)} placeholder="00:00" className="w-20 bg-transparent text-xs text-gray-400 focus:outline-none" />
                                <input value={l.video || ""} onChange={(e) => setLesson(i, li, "video", e.target.value)} placeholder="video url" className="w-48 bg-transparent text-xs text-gray-500 focus:outline-none" />
                                <button onClick={() => removeLesson(i, li)} className="text-gray-500 hover:text-red-400"><X className="w-4 h-4" /></button>
                            </div>
                        ))}
                    </div>
                    <button data-testid={`add-lesson-${i}`} onClick={() => addLesson(i)} className="mt-3 inline-flex items-center gap-1.5 text-xs text-lime hover:text-lime-hover"><Plus className="w-3.5 h-3.5" /> Add lesson</button>
                </div>
            ))}
            <button data-testid="add-stage" onClick={addStage} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-sm hover:bg-white/5"><Plus className="w-4 h-4" /> Add stage</button>
        </div>
    );
}

function QuizTab({ c, setC }) {
    const quiz = c.quiz || { title: "", questions: [] };
    const setQuiz = (q) => setC({ ...c, quiz: q });
    const addQ = () => setQuiz({ ...quiz, questions: [...quiz.questions, { id: `q_${Date.now()}`, text: "", options: ["", "", "", ""], correct_index: 0 }] });
    const setQ = (i, field, val) => setQuiz({ ...quiz, questions: quiz.questions.map((q, idx) => idx === i ? { ...q, [field]: val } : q) });
    const setOpt = (i, oi, val) => setQuiz({
        ...quiz, questions: quiz.questions.map((q, idx) => idx === i ? { ...q, options: q.options.map((o, j) => j === oi ? val : o) } : q),
    });
    const removeQ = (i) => setQuiz({ ...quiz, questions: quiz.questions.filter((_, idx) => idx !== i) });

    return (
        <div className="space-y-4">
            <div>
                <label className={labelCls}>Quiz title</label>
                <input value={quiz.title} onChange={(e) => setQuiz({ ...quiz, title: e.target.value })} className={inputCls} />
            </div>
            {quiz.questions.map((q, i) => (
                <div key={i} className="bg-ink-800 border border-white/5 rounded-2xl p-4 space-y-3">
                    <div className="flex items-start gap-2">
                        <textarea value={q.text} onChange={(e) => setQ(i, "text", e.target.value)} placeholder="Question text..." rows={2} className="flex-1 bg-ink-700 rounded-xl p-2 text-sm focus:outline-none" />
                        <button onClick={() => removeQ(i)} className="w-8 h-8 rounded-full hover:bg-red-500/20 hover:text-red-400 text-gray-400 flex items-center justify-center shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    {q.options.map((o, oi) => (
                        <div key={oi} className="flex items-center gap-2">
                            <input type="radio" name={`correct_${i}`} checked={q.correct_index === oi} onChange={() => setQ(i, "correct_index", oi)} className="accent-lime" />
                            <input value={o} onChange={(e) => setOpt(i, oi, e.target.value)} placeholder={`Option ${oi + 1}`} className="flex-1 bg-ink-700 rounded-lg px-3 py-1.5 text-sm focus:outline-none" />
                        </div>
                    ))}
                </div>
            ))}
            <button data-testid="add-question" onClick={addQ} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-sm hover:bg-white/5"><Plus className="w-4 h-4" /> Add question</button>
        </div>
    );
}
